import React, { useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import moment from 'moment';
import { getCurrentDateObject, Heading, Types } from '@marriott/mi-ui-library';
import {
  GetEventsByMiniHotelInput,
  RoomingListEvent,
  GetEventsByMiniHotelDttInput,
  ActiveStatus,
} from '@marriott/mi-groups-graphql';
import { Dropdown, TextFormField } from '../../../molecules';
import { DropdownOption } from '../../../molecules/Dropdown/Dropdown.types';
import { useAssociateStore } from '../../store';
import { getFormattedCalendarDate, getFormattedDate } from '../../../utils';
import { getClusterCodeOrPropertyId, getPropertyId } from '../../utils/roomingList';
import { useLocaleStore, useDttStore } from '../../../store';
import { EventInfoProps, EventInfoVariant } from './EventInfo.types';
import { EventInfoContainer } from './EventInfo.styles';
import { MINI_HOTEL_CODE_INVALID_ERROR } from '../../constants';
import { useGetEventsByMiniHotel } from './EventInfo.transformer';

export const EventInfo: React.FC<EventInfoProps> = ({
  variant,
  eventInfo,
  control,
  setValue,
  setEventData,
  errors,
  showRequiredError = false,
  setError,
  clearErrors,
  onApiError,
}) => {
  const {
    heading,
    miniHotelCodeLabel,
    miniHotelCodeLabelDtt,
    miniHotelCodeRequiredError,
    miniHotelCodeRequiredErrorDtt,
    miniHotelInvalidError,
    miniHotelInvalidErrorDtt,
    miniHotelCodeHelperText,
    miniHotelCodeHelperTextDtt,
    eventDatesLabel,
    eventDatesHelperLabel,
    eventNameLabel,
  } = eventInfo;
  const showHeading = !!heading;

  const { isDtt } = useDttStore();
  const { locale } = useLocaleStore();
  const { marshaCode } = useAssociateStore();

  const [eventDetailList, setEventDetailList] = useState<RoomingListEvent[]>([]);
  const [eventDateOptions, setEventDateOptions] = useState<DropdownOption<RoomingListEvent>[]>([]);

  const [getEventsByMiniHotel, { loading, error }] = useGetEventsByMiniHotel();

  const encodedAbovePropertyCodeOrPropertyCode = btoa(getClusterCodeOrPropertyId(marshaCode));

  useEffect(() => {
    if (eventDetailList.length > 1) {
      const miniHotelCodeDetails: DropdownOption<RoomingListEvent>[] = eventDetailList.map(event => {
        const { arrive, depart } = event.groupEventSummary;
        return {
          label: getEventDate(arrive, depart),
          value: arrive,
          ...event,
        };
      });
      setEventDateOptions(miniHotelCodeDetails);
    }
  }, [eventDetailList]);

  useEffect(() => {
    onApiError(!!error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const showEventNameAndDates =
    variant === EventInfoVariant.CREATE_EVENT ||
    variant === EventInfoVariant.MODIFY_EVENT ||
    (variant === EventInfoVariant.UPLOAD_TO_MARSHA && eventDetailList.length);

  const getEventsByMiniHotelHandler = async (miniHotelCode: string) => {
    const isMiniHotelCodeInvalid = isDtt ? miniHotelCode.length < 3 : miniHotelCode.length !== 3;
    if (isMiniHotelCodeInvalid) {
      setError('miniHotelCode', {
        type: 'required',
        message: isDtt ? miniHotelInvalidErrorDtt : miniHotelInvalidError,
      });
      return;
    }

    const input: GetEventsByMiniHotelInput | GetEventsByMiniHotelDttInput = {
      propertyId: getPropertyId(marshaCode),
      ...(isDtt
        ? {
            groupBlockCode: miniHotelCode,
            startDate: getFormattedCalendarDate(
              getFormattedDate(getCurrentDateObject()),
              'hyphenatedDateWithMonthNoAndYear'
            ),
            activeStatus: ActiveStatus.ACTIVE,
            groupBlockCodeExactMatch: true,
          }
        : { miniHotelCode }),
    };

    try {
      await getEventsByMiniHotel({
        variables: { input, plannerDetails: variant === EventInfoVariant.MODIFY_EVENT },
        context: {
          headers: {
            'accept-language': locale,
            'x-associate-info': `X-Billing-Code=${encodedAbovePropertyCodeOrPropertyCode};X-Location-Code=${encodedAbovePropertyCodeOrPropertyCode};`,
          },
        },
        fetchPolicy: 'no-cache',
        errorPolicy: 'ignore',
        onCompleted: data => {
          if (data) {
            const { roomingListEvents, errors } =
              data?.hotelOperations?.groupManagement?.roomingListEventsByGroupCode || {};
            if (roomingListEvents) {
              const { title, groupEventSummary } = roomingListEvents[0];
              setValue('eventName', title);
              setValue('eventDates', getEventDate(groupEventSummary?.arrive, groupEventSummary?.depart));
              setEventData?.(roomingListEvents[0]);
              setEventDetailList(roomingListEvents);
              clearErrors('miniHotelCode');
            } else if (errors?.length) {
              const errorMessage = errors?.[0]?.message || MINI_HOTEL_CODE_INVALID_ERROR;
              setError('miniHotelCode', { type: 'required', message: errorMessage });
            }
          }
        },
      });
    } catch (error) {
      console.error((error as Error).message);
    }
  };

  const reset = () => {
    setValue('eventName', '');
    setValue('eventDates', '');
    setEventData?.(null);
    setEventDetailList([]);
  };

  const getEventDate = (startDate: string, endDate: string) => {
    const eventStartDate = getFormattedDate(moment(startDate), 'dateWithDayMonthAndYear');
    const eventEndDate = getFormattedDate(moment(endDate), 'dateWithDayMonthAndYear');
    return `${eventStartDate} - ${eventEndDate}`;
  };

  const handleEventDatesChange = (option: DropdownOption<RoomingListEvent>) => {
    setValue('eventName', option.title);
    setEventData?.(option);
  };

  const miniHotelCodeField = (
    <Controller
      name="miniHotelCode"
      control={control}
      rules={{
        required: showRequiredError && `${isDtt ? miniHotelCodeRequiredErrorDtt : miniHotelCodeRequiredError}`,
      }}
      render={({ field }) => (
        <TextFormField
          {...field}
          label={isDtt ? miniHotelCodeLabelDtt : miniHotelCodeLabel}
          ariaLabel={isDtt ? miniHotelCodeLabelDtt : miniHotelCodeLabel}
          className="m-input-field mb-5 mini-hotel-code"
          maxLength={10}
          showErrorMessage={!!errors?.['miniHotelCode']?.message}
          errorMessage={errors?.['miniHotelCode']?.message}
          showHelperText={!errors?.['miniHotelCode']?.message}
          helperText={isDtt ? miniHotelCodeHelperTextDtt : miniHotelCodeHelperText}
          showIcon={loading}
          iconClass="icon icon-spinner"
          testId="miniHotelCode"
          onChange={event => {
            field.onChange(event);
            if (!event.target.value) {
              clearErrors('miniHotelCode');
            }
          }}
          onBlur={event => {
            field.onChange(event);
            reset();
            const miniHotel = event.target.value;
            miniHotel && getEventsByMiniHotelHandler(miniHotel);
          }}
        />
      )}
    />
  );

  const eventDatesField = (
    <Controller
      name="eventDates"
      control={control}
      render={({ field }) =>
        eventDetailList.length > 1 ? (
          <Dropdown
            name="eventDates"
            label={eventDatesLabel}
            ariaLabel={eventDatesLabel}
            options={eventDateOptions}
            selectedValue={eventDateOptions[0]?.value}
            helperText={eventDatesHelperLabel}
            className="mb-5"
            onChange={event => {
              field.onChange(event);
              handleEventDatesChange(event);
            }}
          />
        ) : (
          <TextFormField
            {...field}
            label={eventDatesLabel}
            ariaLabel={eventDatesLabel}
            className="m-input-field mb-5"
            disabled={true}
          />
        )
      }
    />
  );

  const eventNameField = (
    <Controller
      name="eventName"
      control={control}
      render={({ field }) => (
        <TextFormField
          {...field}
          label={eventNameLabel}
          ariaLabel={eventNameLabel}
          className="m-input-field mb-5"
          disabled={true}
          testId="eventName"
          onChange={event => {
            field.onChange(event);
            clearErrors('eventName');
          }}
        />
      )}
    />
  );

  return (
    <EventInfoContainer className="row mx-2 mx-sm-5">
      {showHeading && (
        <div className="col-12 my-3 px-0">
          <Heading
            fontSize={Types.size.small}
            titleText={heading}
            variation={Types.headingType.title}
            element={Types.tags.h2}
          />
        </div>
      )}
      <div className="col-md-6 col-lg-4">{miniHotelCodeField}</div>
      {showEventNameAndDates ? (
        <>
          <div className="col-md-6 col-lg-4">{eventDatesField}</div>
          <div className="col-md-6 col-lg-4">{eventNameField}</div>
        </>
      ) : null}
    </EventInfoContainer>
  );
};
